import { useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { Close, CloseRounded, SearchRounded } from '@mui/icons-material';
import { SpeakerPreferenceRow } from './SettingsPreferenceComponents';
import { searchSpeakers } from '../utils/speakerSearch';

const SearchSpeakersDrawer = ({
    open,
    onClose,
    onOpenSpeaker,
    onSaveSpeaker,
    statusFor,
    preferencesReady = true,
    initialState,
    saveError,
    onDismissSaveError,
}) => {
    const [query, setQuery] = useState(initialState?.query || '');
    const [speakers, setSpeakers] = useState(initialState?.speakers || []);
    const [completedQuery, setCompletedQuery] = useState(initialState ? (initialState.query || '').trim() : null);
    const [error, setError] = useState(false);
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const restoreScrollRef = useRef(null);
    const initialStateRef = useRef(initialState);
    initialStateRef.current = initialState;
    const trimmedQuery = query.trim();

    useEffect(() => {
        if (!open) return;
        const restored = initialStateRef.current;
        setQuery(restored?.query || '');
        setSpeakers(restored?.speakers || []);
        setCompletedQuery(restored ? (restored.query || '').trim() : null);
        setError(false);
        restoreScrollRef.current = restored?.scrollTop || 0;
        if (restored) return;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 100);
        return () => window.clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (!open || !trimmedQuery || completedQuery === trimmedQuery) return;
        let active = true;
        setError(false);
        const timer = window.setTimeout(async () => {
            try {
                const results = await searchSpeakers(trimmedQuery);
                if (!active) return;
                setSpeakers(results);
                setCompletedQuery(trimmedQuery);
            } catch (err) {
                if (active) {
                    setError(true);
                    setCompletedQuery(trimmedQuery);
                }
            }
        }, 250);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [open, trimmedQuery, completedQuery]);

    useEffect(() => {
        if (open && completedQuery === trimmedQuery && restoreScrollRef.current !== null) {
            if (scrollRef.current) scrollRef.current.scrollTop = restoreScrollRef.current;
            restoreScrollRef.current = null;
        }
    }, [open, completedQuery, trimmedQuery]);

    const changeQuery = (value) => {
        restoreScrollRef.current = null;
        setQuery(value);
        setCompletedQuery(null);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    };
    const openSpeaker = (speaker) => {
        onOpenSpeaker(speaker, { query, speakers, scrollTop: scrollRef.current?.scrollTop || 0 });
    };
    const searching = Boolean(trimmedQuery) && completedQuery !== trimmedQuery;

    return (
        <Drawer anchor='bottom' open={open} onClose={onClose}
            PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'add-speakers-title' }}>
            <div className='appDrawerSurface addShowsDrawer addSpeakersDrawer'>
                <div className='addShowsHeader'>
                    <h2 id='add-speakers-title'>Add Speakers</h2>
                    <button type='button' aria-label='Close Add Speakers' onClick={onClose} className='addShowsClose'>
                        <Close />
                    </button>
                </div>
                <div className='appSearchBoxField addShowsSearch' role='search'>
                    <SearchRounded className='appSearchBoxLeadingIcon' aria-hidden='true' />
                    <input ref={inputRef} className='appSearchBoxInput' type='search'
                        aria-label='Search speakers' placeholder='Search speakers'
                        value={query} onChange={(event) => changeQuery(event.target.value)} />
                    {query && (
                        <button type='button' className='appSearchBoxClearButton' aria-label='Clear search'
                            onClick={() => {
                                changeQuery('');
                                inputRef.current?.focus();
                            }}>
                            <CloseRounded fontSize='small' aria-hidden='true' />
                        </button>
                    )}
                </div>
                {saveError && (
                    <div className='addShowsMessage' role='alert'>
                        <p>{saveError}</p>
                        <button type='button' onClick={onDismissSaveError}>Dismiss</button>
                    </div>
                )}
                <div className='addShowsResults' ref={scrollRef}>
                    {!trimmedQuery ? (
                        <div className='addShowsEmptyState'>
                            <SearchRounded className='addShowsEmptyIcon' aria-hidden='true' />
                            <h3>Find your next speaker</h3>
                            <p>Search by name to get started.</p>
                        </div>
                    ) : searching ? (
                        <p className='addShowsMessage' role='status'>Searching speakers…</p>
                    ) : error ? (
                        <div className='addShowsMessage' role='alert'>
                            <p>Couldn’t load speakers.</p>
                            <button type='button' onClick={() => setCompletedQuery(null)}>Retry search</button>
                        </div>
                    ) : speakers.length === 0 ? (
                        <p className='addShowsMessage'>No speakers matching “{trimmedQuery}”.</p>
                    ) : (
                        <div className='settingsPreferenceRows'>
                            {speakers.map((speaker) => (
                                <SpeakerPreferenceRow
                                    key={speaker.id}
                                    speaker={speaker}
                                    description={speaker.description}
                                    status={statusFor(speaker)}
                                    disabled={!preferencesReady}
                                    onOpen={() => openSpeaker(speaker)}
                                    onStatusChange={(status) => onSaveSpeaker(speaker, status)}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div className='addShowsFooter'>
                    <button type='button' className='closeFooterButton' onClick={onClose}>Done</button>
                </div>
            </div>
        </Drawer>
    );
};

export default SearchSpeakersDrawer;
